import { useEffect, useState } from "react";
import { Zap, Copy, Check, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function BuyModal({ listing, onClose, onOrderCreated }) {
  const [step, setStep] = useState("idle"); // idle | loading | invoice | error
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!listing) return;
    (async () => {
      setStep("loading");
      try {
        const { data } = await api.post(`/listings/${listing.id}/buy`, {});
        setOrder(data);
        setStep("invoice");
        onOrderCreated?.(data);
      } catch (err) {
        setError(formatApiError(err));
        setStep("error");
      }
    })();
  }, [listing]);

  const copyInvoice = () => {
    if (!order) return;
    try {
      navigator.clipboard.writeText(order.bolt11);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Copy failed — long-press the invoice text to select manually.");
    }
  };

  const markPaid = async () => {
    try {
      const { data } = await api.post(`/orders/${order.id}/mark-paid`);
      setOrder(data);
      toast.success("Marked as paid. Waiting for seller confirmation.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!listing) return null;

  const qrUrl = order?.bolt11
    ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&data=lightning:${order.bolt11}`
    : null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="buy-modal"
        className="bg-[#FDFBF7] border-2 border-black w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b-2 border-black flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#F7931A]" />
            <div className="font-display font-black uppercase text-xl">Pay with Lightning</div>
          </div>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6">
          <div className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
            {listing.title}
          </div>
          <div className="font-display font-black text-4xl mt-1 tracking-tighter">
            {listing.btc_price} <span className="text-[#F7931A]">BTC</span>
          </div>

          {step === "loading" && (
            <div className="mt-8 py-16 text-center font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
              Requesting invoice from seller's wallet...
            </div>
          )}

          {step === "error" && (
            <div className="mt-6 p-4 border-2 border-black bg-white">
              <div className="font-bold text-sm">Couldn&apos;t create invoice</div>
              <div className="text-sm mt-2 text-[#0A0A0A]/80">{error}</div>
              <div className="mt-3 font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
                Ask the seller to add a Lightning Address on their Profile.
              </div>
            </div>
          )}

          {step === "invoice" && order && (
            <>
              <div className="mt-6 flex justify-center">
                <img src={qrUrl} alt="Lightning invoice QR" data-testid="buy-qr" className="border-4 border-black" />
              </div>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">
                Invoice (BOLT11)
              </div>
              <div
                data-testid="buy-bolt11"
                className="font-mono text-[10px] break-all bg-white border-2 border-black p-3 mt-2 max-h-32 overflow-y-auto"
              >
                {order.bolt11}
              </div>
              <button
                onClick={copyInvoice}
                data-testid="btn-copy-invoice"
                className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest border-2 border-black px-3 py-2 bg-white hover:bg-black hover:text-[#FDFBF7] transition-colors"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied" : "Copy invoice"}
              </button>

              <div className="mt-6 border-t-2 border-black pt-6">
                <div className="text-xs font-bold uppercase tracking-widest mb-2">Paid it?</div>
                <button
                  onClick={markPaid}
                  data-testid="btn-mark-paid"
                  className="w-full py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest btn-brutal"
                >
                  I&apos;ve paid · notify seller
                </button>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">
                  Seller will confirm and release the item.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
